// Package imports:
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
// Component imports:
import DisplayBox from '../DisplayBox/DisplayBox';
import Loading from '../../ui-elements/Loading/Loading';
import Alert from '../../ui-elements/Alert/Alert';
// Service imports:
import { getTableDate, convertToPercentage } from '../../services/utils';
import { useApiLmdData } from '../../services/apiHooks';
// Type imports:
import { IDefaultProps } from '../../types/Types';
import { IApiLmdShareholder } from '../../types/HlutabrefTypes';

const COLORS = ['#4569EE','#6CD3FF','#94EFB3','#FFF06B','#FF7697','#B07EF0'];

interface IChartDatum {
    name: string | undefined,
    y: number | undefined
}

interface IOwnProps {
    symbol: string,
    limit?: number
}

type Props = IOwnProps & IDefaultProps & React.HTMLAttributes<HTMLDivElement>;

const Shareholders: React.FC<Props> = ({
    symbol,
    limit = 5,
    accessToken,
    refreshRateMs,
    ...props
}) => {
    const [data] = useApiLmdData<IApiLmdShareholder[]>(
        `/v1/market_data/v1/${symbol}/shareholders`,
        accessToken,
        refreshRateMs
    );
    const [chartData, setChartData] = useState<IChartDatum[]>([]);
    // Div references.
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const newChartData: IChartDatum[] = [];

        if (data.data !== null && !(data.error instanceof Error)) {
            data.data.sort((a, b) => {
                return a.Percentage === null && b.Percentage === null
                    ? 0
                    : a.Percentage === null
                    ? 1
                    : b.Percentage === null
                    ? -1
                    : a.Percentage < b.Percentage
                    ? 1
                    : a.Percentage > b.Percentage
                    ? -1
                    : 0
            });
            let sum = 0;
            for (let datum of data.data.slice(0,limit)) {
                newChartData.push({
                    name: datum.Owner ?? undefined,
                    y: datum.Percentage ?? undefined
                });
                sum += datum.Percentage ?? 0;
            }
            if (newChartData.length === limit) {
                newChartData.push({
                    name: 'Aðrir',
                    y: 1 - sum
                });
            }
            setChartData(newChartData);
        }

        if (divRef.current !== null) {
            Highcharts.chart(divRef.current, {
                chart: {
                    type: 'pie',
                    height: 280,
                    style: {
                        fontFamily: "Roboto"
                    },
                    animation: false
                },
                title: {
                    text: '',
                },
                plotOptions: {
                    pie: {
                        size: '100%',
                        center: ['50%','50%'],
                        animation: false
                    }
                },
                series: [{
                    type: 'pie',
                    innerSize: '40%',
                    data: chartData,
                    dataLabels: {
                        enabled: false
                    },
                    showInLegend: true,
                    colors: COLORS
                }],
                exporting: {
                    enabled: false
                },
                credits: {
                    enabled: false
                },
                tooltip: {
                    enabled: false
                },
                legend: {
                    enabled: false
                }
            }, () => {});
        }
    }, [data, divRef.current]);
    
    return (
        <DisplayBox
            className='KCL_shareholders'
            title="Hluthafar"
            asideComponent={data.error instanceof Error || data.data === null || data.data.length === 0 ? undefined
                : <span className="date">
                    Uppfært {getTableDate(data.data[0].ValueDate, 'DD/MM/YYYY', '.')}
                </span>
            }
            {...props}
        >
            {data.data === null
                ? <Loading />
                : data.error instanceof Error
                ? <Alert type="error" headText={data.error.message} />
                : data.data.length === 0
                ? <Alert type='info' headText='Gögn vantar' />
                : <>
                    <div className="shareholders-chart" ref={divRef}></div>
                    <div className="shareholders-legend">
                        {chartData.map((d, i) => (
                            <div className="legend-item" key={i}>
                                <span className="color" style={{background: COLORS[i]}}></span>
                                <span className="text">{d.name} {convertToPercentage(d.y ?? null, true)}</span>
                            </div>
                        ))}
                    </div>
                </>
            }
        </DisplayBox>
    );
}

export default Shareholders;